// Publishes marketing-form events to Shopify customer events for a custom pixel (docs/ANALYTICS-FORMS.md).
// Names are mtn:-prefixed: form_submitted is a reserved standard pixel event.
// Delegation lives here rather than in mtn.js (05-javascript.md) so pages without a
// tracked form never load it; each section or block that owns one adds the script tag.
//
// Two outcome paths: native Shopify forms POST and redirect, so their result is
// server-rendered markup read once on load; Klaviyo forms submit over fetch and
// announce themselves with the OUTCOME_EVENT below.

const EVENT_NAMESPACE = 'mtn:';
const FORM_SELECTOR = '[data-analytics-form]';
const RESULT_SELECTOR = '[data-analytics-result]';
const OUTCOME_EVENT = 'mtn:form-outcome';
const STORAGE_PREFIX = 'mtn:form-analytics:';
const RETRY_INTERVAL = 200;
const MAX_RETRIES = 25;

/**
 * @typedef {Object} FormMeta
 * @property {string} form_id - Stable slug identifying the form.
 * @property {string} form_name - Human-readable name shown in reports.
 * @property {string} form_type - Shopify form type (contact, customer, …).
 */

/** @type {WeakSet<Element>} */
const startedForms = new WeakSet();

/** @type {Array<{ name: string, payload: Record<string, string> }>} */
const pendingEvents = [];

let retries = 0;
let retryTimer = null;

const getAnalytics = () => {
  const analytics = window.Shopify?.analytics;
  return typeof analytics?.publish === 'function' ? analytics : null;
};

/**
 * @param {HTMLElement} element
 * @returns {FormMeta}
 */
const readMeta = (element) => ({
  form_id: element.dataset.analyticsFormId || '',
  form_name: element.dataset.analyticsFormName || '',
  form_type: element.dataset.analyticsFormType || '',
});

/**
 * @param {FormMeta} meta
 * @param {string} [errors] - Comma-separated field names that failed validation.
 * @returns {Record<string, string>}
 */
const buildPayload = (meta, errors) => {
  const payload = {
    form_id: meta.form_id,
    form_name: meta.form_name,
    form_type: meta.form_type,
    page_path: `${window.location.pathname}${window.location.search}`,
    page_title: document.title,
  };

  if (errors) payload.errors = errors;

  return payload;
};

const flush = () => {
  retryTimer = null;

  const analytics = getAnalytics();

  if (!analytics) {
    if (retries >= MAX_RETRIES) {
      pendingEvents.length = 0;
      return;
    }

    retries += 1;
    retryTimer = window.setTimeout(flush, RETRY_INTERVAL);
    return;
  }

  while (pendingEvents.length) {
    const event = pendingEvents.shift();

    try {
      const result = analytics.publish(`${EVENT_NAMESPACE}${event.name}`, event.payload);
      if (result && typeof result.catch === 'function') result.catch(() => {});
    } catch {
      // Analytics must never break the page.
    }
  }
};

/**
 * @param {string} name
 * @param {Record<string, string>} payload
 */
const publish = (name, payload) => {
  pendingEvents.push({ name, payload });
  if (retryTimer === null) {
    retries = 0;
    flush();
  }
};

const navigationType = () => {
  const entry = performance.getEntriesByType?.('navigation')?.[0];
  return entry ? entry.type : '';
};

/** @param {string} key */
const alreadyReported = (key) => {
  try {
    return window.sessionStorage.getItem(key) !== null;
  } catch {
    return false;
  }
};

/** @param {string} key */
const markReported = (key) => {
  try {
    window.sessionStorage.setItem(key, '1');
  } catch {
    // Storage can be unavailable in private mode.
  }
};

/**
 * Reads the server-rendered outcome markers and publishes one event per form.
 * Shopify forms are native POSTs that redirect back to the page, so the outcome
 * exists only as markup: count it on that navigation, not on reload/back-forward.
 * Browsers without Navigation Timing fall back to a per-URL session guard.
 */
const reportResults = () => {
  if (window.Shopify?.designMode) return;

  const navType = navigationType();
  if (navType === 'reload' || navType === 'back_forward') return;

  for (const marker of document.querySelectorAll(RESULT_SELECTOR)) {
    const result = marker.dataset.analyticsResult;
    if (result !== 'success' && result !== 'error') continue;

    const source = marker.dataset.analyticsFormId ? marker : marker.closest(FORM_SELECTOR);
    if (!source) continue;

    const meta = readMeta(source);
    if (!meta.form_id) continue;

    const errors = marker.dataset.analyticsErrors || '';

    if (!navType) {
      const key = `${STORAGE_PREFIX}${window.location.pathname}${window.location.search}:${meta.form_id}:${result}`;
      if (alreadyReported(key)) continue;
      markReported(key);
    }

    publish(result === 'success' ? 'form_submitted' : 'form_failed', buildPayload(meta, errors));
  }
};

/** @param {Event} event */
const handleStart = (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const form = target.closest(FORM_SELECTOR);
  if (!form || startedForms.has(form)) return;
  if (form.dataset.analyticsTrackStart === 'false') return;

  startedForms.add(form);
  publish('form_started', buildPayload(readMeta(form)));
};

/**
 * @param {Event} event
 */
const handleOutcome = (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const form = target.closest(FORM_SELECTOR);
  if (!form || !(form instanceof HTMLElement)) return;

  const meta = readMeta(form);
  if (!meta.form_id) return;

  const { result, errors } = /** @type {CustomEvent} */ (event).detail ?? {};
  if (result !== 'success' && result !== 'error') return;

  publish(result === 'success' ? 'form_submitted' : 'form_failed', buildPayload(meta, errors));
};

document.addEventListener('focusin', handleStart);
document.addEventListener('input', handleStart);
document.addEventListener(OUTCOME_EVENT, handleOutcome);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', reportResults, { once: true });
} else {
  reportResults();
}
